import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { App } from "antd5";

import PaywallCTAModal from "components/account_management/PaywallCTAModal";
import { generateNoticeSearchUrl } from "components/notices/utils";
import Card from "components/ui/card/Card";
import Collapsible from "components/ui/collapsible/Collapsible";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { ViewConfigurationFilterSettingsRelevanceScoreEnum as RelevanceScoreEnum } from "lib/generated/app-api";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { useSubscription } from "lib/hooks/subscription";
import { useDialogManager } from "lib/providers/DialogManager";
import {
  sysBackgroundAlternative,
  sysPrimaryPressed,
  sysTextInactive,
  sysTextSecondary,
} from "lib/themes/colors";
import { OpportunityBuyerActivityStatus } from "lib/types/graphQLEnums";
import { isDefined, pluralise } from "lib/utils";
import { getFromToDateRange } from "lib/utils/dateTimeUtils";
import { toHumanRelativeTimeFromNow } from "lib/utils/relative_time";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { useRegenerateBuyerActivity } from "../../../lib/hooks/api/opportunities/useRegenerateBuyerActivity";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { Flex, Text } from "../../../styles/utility-components";
import { Markdown } from "../../reports/builder_components/Markdown";
import Button from "../../ui/button/Button";
import FindEntity from "../saved/FindEntity";
import { RecommendedNotices } from "./RecommendedNotices";

export function BuyerActivity({ opportunity }: { opportunity: OpportunityDetails }) {
  const { message } = App.useApp();
  const { colors } = useTheme();
  const dialogManager = useDialogManager();

  const subscription = useSubscription();
  const isFreeOrBasic = !subscription.hasDataTypes("BUYERS|SUPPLIERS");

  const { mutate: actionPql } = usePqlActioned();

  const { mutate: regenerate, isLoading: isManuallyRegenerating } = useRegenerateBuyerActivity({
    onSuccess: () => message.success("Buyer activity regenerated"),
    onError: () => message.error("Failed to regenerate buyer activity"),
  });

  const isBuyerActivityPending =
    opportunity.buyerActivity?.status === OpportunityBuyerActivityStatus.Pending;

  const lastRefreshed = opportunity.buyerActivity?.createdAt
    ? toHumanRelativeTimeFromNow(opportunity.buyerActivity?.createdAt)
    : null;

  const buyerIds = opportunity.buyers?.map((buyer) => buyer.id) ?? [];

  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: buyerIds,
  });

  const handleRequestDemo = () => {
    actionPql({
      actionType: "Book a demo in-app",
      dataType: "OPPORTUNITY",
      pageUrl: window.location.href,
    });

    void dialogManager.openDialog(PaywallCTAModal, {
      showBookADemoForm: true,
      featureType: "AWARDS",
    });
  };

  const handleComparePlans = () => {
    actionPql({
      actionType: "Compare all plans",
      dataType: "OPPORTUNITY",
      pageUrl: window.location.href,
    });

    void dialogManager.openDialog(PaywallCTAModal, {
      showBookADemoForm: false,
      featureType: "AWARDS",
    });
  };

  return (
    <Flex column gap={24} aria-label="buyer-activity">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="flex-start" gap={8}>
          <IconContainer bgColor={sysBackgroundAlternative} size={24} padding={8}>
            <UIcon icon="aiBuilding" size={16} />
          </IconContainer>
          <Flex column gap={6}>
            <Text h2>Historical buyer activity</Text>
            {lastRefreshed && (
              <Flex gap={6} alignItems="center">
                <Text color={sysTextSecondary} description>
                  Last refreshed {lastRefreshed}. Stotles AI can make mistakes.
                </Text>
                <UIcon size={12} color={sysTextInactive} icon="refreshCw05" />
              </Flex>
            )}
          </Flex>
        </Flex>
        {window.currentUser?.admin && (
          <Button
            onClick={() => regenerate({ id: opportunity.id })}
            disabled={isManuallyRegenerating}
            buttonType="secondary"
            icon="refreshCcw01"
          >
            {!isManuallyRegenerating ? "Regenerate" : "Regenerating..."}
          </Button>
        )}
      </Flex>
      {isFreeOrBasic && (
        <Card backgroundColor={colors.sysTextDefault}>
          <Flex justifyContent="space-between" alignItems="center">
            <Flex gap={8} alignItems="center">
              <UIcon icon="lock01" size={20} color="white" />
              <Text description white>
                This feature is now free for everyone - limited time only!
              </Text>
            </Flex>
            <Flex gap={8} alignItems="center">
              <Button buttonStyle="inverse" onClick={handleRequestDemo}>
                Request a demo
              </Button>
              <Button buttonType="secondary" buttonStyle="inverse" onClick={handleComparePlans}>
                Compare all plans
              </Button>
            </Flex>
          </Flex>
        </Card>
      )}
      <BuyerActivitySummaryContainer
        opportunity={opportunity}
        noticesSearchUrl={noticesSearchUrl}
        isLoading={isBuyerActivityPending || isManuallyRegenerating}
      />
    </Flex>
  );
}

function BuyerActivitySummaryContainer({
  opportunity,
  noticesSearchUrl,
  isLoading,
}: {
  opportunity: OpportunityDetails;
  noticesSearchUrl: string;
  isLoading: boolean;
}) {
  const hasExistingNotices = opportunity.notices.length > 0;

  if (!hasExistingNotices) {
    return <FindEntity entity="notice" url={noticesSearchUrl} isEmptyState />;
  }

  return <BuyerActivitySummary opportunity={opportunity} isLoading={isLoading} />;
}

function BuyerActivitySummary({
  opportunity,
  isLoading,
}: {
  opportunity: OpportunityDetails;
  isLoading: boolean;
}) {
  const [isSimilarNoticesOpen, setIsSimilarNoticesOpen] = useState(true);

  const relatedNotices = opportunity.buyerActivity?.relatedNotices ?? [];
  const allNotices = [...relatedNotices, ...opportunity.notices];
  const noticesIdentifiedCount = allNotices.length;

  const noticePublishDates = allNotices.map((notice) => notice.publishDate).filter(isDefined);

  const publishDateRange = getFromToDateRange(noticePublishDates);
  const fromPublishYear = publishDateRange?.fromDate.getFullYear();
  const toPublishYear = publishDateRange?.toDate.getFullYear();

  return (
    <>
      <Collapsible
        title={
          <Flex gap={14}>
            <IconContainer>
              <UIcon icon="aiStars" color={sysPrimaryPressed} size={16} />
            </IconContainer>
            <Flex column gap={4}>
              <Text h2>AI summary of this buyer's historic activity</Text>
              <SimilarNoticesInfo
                isLoading={isLoading}
                noticeCount={noticesIdentifiedCount}
                fromPublishYear={fromPublishYear}
                toPublishYear={toPublishYear}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsSimilarNoticesOpen(true);
                }}
              />
            </Flex>
          </Flex>
        }
        defaultOpen={true}
        isBordered
        chevronPosition="top"
        headerContentProps={{ padding: "24px 0" }}
      >
        <QuestionsContainer column>
          {opportunity.buyerActivity?.questions.map((question) => (
            <Flex key={question.question} column>
              <Text h3>{question.question}</Text>
              {isLoading ? (
                <>
                  <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                  <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                  <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                  <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                </>
              ) : (
                <Markdown text={question.answer} />
              )}
            </Flex>
          ))}
        </QuestionsContainer>
      </Collapsible>
      <Collapsible
        open={isSimilarNoticesOpen}
        onOpenChange={setIsSimilarNoticesOpen}
        title={
          <Flex alignItems="center" gap={14}>
            <IconContainer>
              <UIcon icon="aiFile" color={sysPrimaryPressed} size={16} />
            </IconContainer>
            {isLoading ? (
              <SkeletonText width="255px" fontSize={25} variant="rectangular" />
            ) : (
              <Text h2>
                {`${pluralise(noticesIdentifiedCount, "Notice")} for this product/service identified`}
              </Text>
            )}
          </Flex>
        }
        isBordered
        headerContentProps={{ padding: "24px 0" }}
      >
        <CollapsibleContent>
          <RecommendedNotices
            relatedNotices={relatedNotices}
            savedNotices={opportunity.notices}
            isLoading={isLoading}
          />
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}

function SimilarNoticesInfo({
  noticeCount,
  fromPublishYear,
  toPublishYear,
  onClick,
  isLoading,
}: {
  noticeCount: number;
  fromPublishYear?: number;
  toPublishYear?: number;
  onClick: (e: React.MouseEvent) => void;
  isLoading: boolean;
}) {
  const publishDateRangeLabel =
    fromPublishYear && toPublishYear
      ? getPublishDateRangeLabel(fromPublishYear, toPublishYear)
      : null;

  if (isLoading) {
    return <SkeletonText width="100%" fontSize={21} variant="rectangular" />;
  }

  if (noticeCount === 0) {
    return null;
  }

  return (
    <Text fontWeight="500" color={sysTextSecondary} aria-label="similar-notices-info">
      Based on{" "}
      <ActionText onClick={onClick}>
        {pluralise(noticeCount, "notice")} related to this product/service
      </ActionText>{" "}
      {publishDateRangeLabel}
    </Text>
  );
}

function getPublishDateRangeLabel(fromPublishYear: number, toPublishYear: number) {
  if (fromPublishYear === toPublishYear) {
    return `published in ${fromPublishYear}`;
  }
  return `published between ${fromPublishYear} and ${toPublishYear}`;
}

const IconContainer = styled(Flex)<{ bgColor?: string; size?: number; padding?: number }>(
  ({ theme, bgColor, size = 32, padding = 0 }) => ({
    gap: 8,
    padding: padding,
    backgroundColor: bgColor ?? theme.colors.sysPrimarySubtle,
    height: size,
    width: size,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
  }),
);

const ActionText = styled.span(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  cursor: "pointer",
  "&:hover": {
    color: theme.colors.sysPrimaryPressed,
    textDecoration: "underline",
  },
}));

const CollapsibleContent = styled(Flex)({
  paddingTop: 24,
});

const QuestionsContainer = styled(Flex)({
  marginTop: 24,
  gap: 16,
});
